import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { FilterPill } from './FilterPill';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Toolbar dropdown for filtering the planning board by:
 *  - Customer (PostNL/DPD/...) — multi-select
 *  - Depot per customer (Oevel, ...) — multi-select, expandable
 *  - Route per depot (STT, ...) — multi-select, expandable
 *  - Employee status (all/available/assigned/leave) — single-select
 *
 * Renders as an absolutely-positioned panel anchored under the toggle button;
 * a transparent backdrop closes it on outside click.
 */
export function FilterPopover({
  board,
  filterCompanyIds,
  setFilterCompanyIds,
  filterDepotIds,
  setFilterDepotIds,
  filterRouteIds,
  setFilterRouteIds,
  empFilter,
  setEmpFilter,
  clearAll,
  close,
}) {
  // Build company → depots → routes index for the hierarchical chip list.
  // Honours customer filter so when the operator selects PostNL only PostNL's
  // depots/routes are listed (otherwise we list everything).
  const tree = useMemo(() => {
    if (!board) return [];
    const showAllCompanies = filterCompanyIds.size === 0;
    const depotsByCompany = new Map();
    for (const d of (board.depots || [])) {
      if (!showAllCompanies && !filterCompanyIds.has(d.company_id)) continue;
      if (!depotsByCompany.has(d.company_id)) depotsByCompany.set(d.company_id, []);
      depotsByCompany.get(d.company_id).push({ ...d, routes: [] });
    }
    const depotById = new Map();
    for (const arr of depotsByCompany.values()) for (const d of arr) depotById.set(d.id, d);
    const noDepotByCompany = new Map(); // company_id → routes[] (depot_id null)
    for (const r of (board.routes || [])) {
      if (!showAllCompanies && !filterCompanyIds.has(r.company_id)) continue;
      if (r.depot_id && depotById.has(r.depot_id)) {
        depotById.get(r.depot_id).routes.push(r);
      } else if (!r.depot_id) {
        if (!noDepotByCompany.has(r.company_id)) noDepotByCompany.set(r.company_id, []);
        noDepotByCompany.get(r.company_id).push(r);
      }
    }
    const out = [];
    for (const c of (board.companies || [])) {
      if (!showAllCompanies && !filterCompanyIds.has(c.id)) continue;
      const depots = depotsByCompany.get(c.id) || [];
      const noDepotRoutes = noDepotByCompany.get(c.id) || [];
      if (depots.length === 0 && noDepotRoutes.length === 0) continue;
      out.push({ ...c, depots, noDepotRoutes });
    }
    return out;
  }, [board, filterCompanyIds]);

  // Per-company expand/collapse state. Default expanded when its company is
  // explicitly selected (operator clearly drilling in) — collapsed otherwise.
  const [openCompanies, setOpenCompanies] = useState(() => new Set());
  const isCompanyOpen = (cid) => openCompanies.has(cid) || filterCompanyIds.has(cid);
  const toggleCompany = (cid) => setOpenCompanies((prev) => {
    const next = new Set(prev);
    if (next.has(cid)) next.delete(cid); else next.add(cid);
    return next;
  });

  const toggleSet = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={close} />
      <div className="absolute top-full left-0 mt-1.5 z-40 w-[460px] max-h-[70vh] overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-4 space-y-4" data-testid="planning-filter-popover">
        <section>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Müşteri (PostNL/DPD/...)</div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
            <FilterPill active={filterCompanyIds.size === 0} onClick={() => { setFilterCompanyIds(new Set()); setFilterDepotIds(new Set()); setFilterRouteIds(new Set()); }} testId="planning-filter-all">Tümü</FilterPill>
            {(board?.companies || []).map((c) => {
              const on = filterCompanyIds.has(c.id);
              return (
                <FilterPill
                  key={c.id}
                  active={on}
                  onClick={() => {
                    const n = new Set(filterCompanyIds);
                    if (on) {
                      n.delete(c.id);
                      // Drop dependent depot/route selections that belong to this company
                      const depotIdsOfCompany = new Set((board?.depots || []).filter((d) => d.company_id === c.id).map((d) => d.id));
                      const routeIdsOfCompany = new Set((board?.routes || []).filter((r) => r.company_id === c.id).map((r) => r.id));
                      setFilterDepotIds((prev) => { const x = new Set(prev); for (const id of depotIdsOfCompany) x.delete(id); return x; });
                      setFilterRouteIds((prev) => { const x = new Set(prev); for (const id of routeIdsOfCompany) x.delete(id); return x; });
                    } else {
                      n.add(c.id);
                    }
                    setFilterCompanyIds(n);
                  }}
                  color="emerald"
                  testId={`planning-filter-${c.slug}`}
                >
                  {c.name}
                </FilterPill>
              );
            })}
          </div>
        </section>

        {tree.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Depo / Rota</div>
              {(filterDepotIds.size > 0 || filterRouteIds.size > 0) && (
                <button
                  className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                  onClick={() => { setFilterDepotIds(new Set()); setFilterRouteIds(new Set()); }}
                  data-testid="planning-filter-clear-depot-route"
                >
                  Depo/Rota temizle
                </button>
              )}
            </div>
            <div className="space-y-2">
              {tree.map((c) => {
                const open = isCompanyOpen(c.id);
                const allDepotRoutes = [...c.depots, ...(c.noDepotRoutes.length > 0 ? [{ id: '__nodepot__', name: 'Depo seçilmemiş', routes: c.noDepotRoutes, company_id: c.id }] : [])];
                return (
                  <div key={c.id} className="border border-slate-200 dark:border-slate-700 rounded-md" data-testid={`planning-filter-tree-${c.slug || c.id}`}>
                    <button
                      type="button"
                      onClick={() => toggleCompany(c.id)}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    >
                      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      <span className="flex-1 text-left">{c.name}</span>
                      <span className="text-[10px] text-slate-400">{allDepotRoutes.length} depo</span>
                    </button>
                    {open && (
                      <div className="px-2 py-2 space-y-2 border-t border-slate-100 dark:border-slate-700">
                        {allDepotRoutes.map((d) => {
                          const realDepot = d.id !== '__nodepot__';
                          const depotOn = realDepot && filterDepotIds.has(d.id);
                          return (
                            <div key={d.id}>
                              <div className="flex items-center gap-1 flex-wrap">
                                {realDepot && (
                                  <FilterPill
                                    active={depotOn}
                                    onClick={() => toggleSet(filterDepotIds, setFilterDepotIds, d.id)}
                                    color="teal"
                                    testId={`planning-filter-depot-${d.id}`}
                                  >
                                    {d.name}
                                  </FilterPill>
                                )}
                                {!realDepot && (
                                  <span className="text-[10px] text-slate-400 italic">{d.name}</span>
                                )}
                              </div>
                              {d.routes.length > 0 && (
                                <div className="flex flex-wrap gap-1 pl-3 mt-1">
                                  {d.routes.map((r) => {
                                    const on = filterRouteIds.has(r.id);
                                    return (
                                      <FilterPill
                                        key={r.id}
                                        active={on}
                                        onClick={() => toggleSet(filterRouteIds, setFilterRouteIds, r.id)}
                                        color="amber"
                                        testId={`planning-filter-route-${r.id}`}
                                      >
                                        {r.route_code}
                                      </FilterPill>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Çalışan Durumu</div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { k: 'all', label: 'Tümü' },
              { k: 'available', label: 'Müsait' },
              { k: 'assigned', label: 'Atanmış' },
              { k: 'leave', label: 'İzinli' },
            ].map((o) => (
              <FilterPill
                key={o.k}
                active={empFilter === o.k}
                onClick={() => setEmpFilter(o.k)}
                color="blue"
                testId={`planning-empfilter-${o.k}`}
              >
                {o.label}
              </FilterPill>
            ))}
          </div>
        </section>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
          <button onClick={clearAll} className="text-[11px] text-slate-500 hover:text-slate-700 underline">
            Tümünü temizle
          </button>
          <Button size="sm" className="h-7" onClick={close}>Tamam</Button>
        </div>
      </div>
    </>
  );
}
